import { IconFileText } from '@tabler/icons-react';
import { FileTypes } from 'database';
import { flatten } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Badge, cn } from 'ui';
import { getHumanFileSize } from 'utils';
import { DocumentEmptyStateIcon } from './DocumentEmptyStateIcon';
import { NextEmptyStateIcon } from 'ui';

const TWENTY_MB_IN_BYTES = 20 * 1024 * 1024;

export const mimetypes: {
  [K in FileTypes]: string[];
} = {
  other: ['*'],
  pdf: ['application/pdf'],
  audio: ['audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/webm'],
  video: ['video/mpeg', 'video/ogg', 'video/webm', 'video/mp4'],
  image: [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/svg+xml',
    'image/webp',
  ],
  document: [
    'text/plain',
    'text/csv',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
};

export interface FileInputProps {
  invalid?: boolean;
  maxFiles?: number;
  disabled?: boolean;
  fileTypes?: FileTypes[];
  maxFileSize?: number;
  onDropFiles: (files: File[]) => void;
  onRejectedFiles?: (
    rejectedFiles: { file: File; reason: 'fileType' | 'fileSize' }[],
  ) => void;
}

const fileTypesLabels: {
  [K in FileTypes]: {
    plural: string;
    singular: string;
  };
} = {
  pdf: {
    plural: 'PDFs',
    singular: 'un PDF',
  },
  document: {
    plural: 'documentos',
    singular: 'un documento',
  },
  image: {
    plural: 'imágenes',
    singular: 'una imagen',
  },
  audio: {
    plural: 'audios',
    singular: 'un audio',
  },
  video: {
    plural: 'videos',
    singular: 'un video',
  },
  other: {
    plural: 'archivos',
    singular: 'cualquier archivo',
  },
};

export const FileLabelBadge = ({ children }: { children: React.ReactNode }) => {
  return (
    <Badge
      className={cn(
        'font-medium text-xs truncate text-center text-foreground max-w-full border-primary bg-transparent h-[1.125rem]',
      )}
    >
      {children}
    </Badge>
  );
};

export const FileInput = ({
  fileTypes = ['image', 'document'],
  maxFileSize = TWENTY_MB_IN_BYTES,
  disabled,
  onDropFiles,
  onRejectedFiles,
  maxFiles = 5,
  invalid,
  label,
  docKey,
  children,
  isFileLoaded,
  className,
  uploadMessage,
  fromCreateClaim,
  preview,
}: FileInputProps & {
  label?: string;
  docKey?: string;
  children?: React.ReactNode;
  isFileLoaded?: boolean;
  className?: string;
  uploadMessage?: string;
  fromCreateClaim?: boolean;
  preview?: string | undefined;
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const labelId = label?.replaceAll(' ', '-').toLowerCase() || '';

  const inputRef = useRef<HTMLInputElement>(null);

  const dataTransferItemsToFiles = useCallback(
    (dataTransferList: DataTransferItemList) => {
      const files: File[] = [];

      for (let i = 0; i < dataTransferList.length; i++) {
        const item = dataTransferList[i];

        const file = item.getAsFile();

        if (file) {
          files.push(file);
        }
      }

      return files;
    },
    [],
  );

  const supportedFileTypes = useMemo(
    () => flatten(fileTypes.map((type) => mimetypes[type])),
    [],
  );

  const handleFile = useCallback(
    (files: File[]) => {
      console.log('files from handleFile args', files);

      if (!files.length) return;

      const rejectedFiles: { file: File; reason: 'fileType' | 'fileSize' }[] =
        [];
      const filteredFiles = files
        .filter((f) => {
          const sizeCheck = f.size <= maxFileSize;
          const filetypeCheck = fileTypes.includes('other' satisfies FileTypes)
            ? true
            : supportedFileTypes.includes(f.type);

          if (!sizeCheck) {
            rejectedFiles.push({ file: f, reason: 'fileSize' });
            return false;
          }

          if (!filetypeCheck) {
            rejectedFiles.push({ file: f, reason: 'fileType' });
            return false;
          }

          return true;
        })
        .slice(0, maxFiles);

      // Report rejected files if callback is provided
      if (rejectedFiles.length > 0 && onRejectedFiles) {
        onRejectedFiles(rejectedFiles);
      }

      onDropFiles(filteredFiles);
    },
    [
      onDropFiles,
      onRejectedFiles,
      maxFileSize,
      fileTypes,
      supportedFileTypes,
      maxFiles,
    ],
  );

  return (
    <div
      onDrop={(ev) => {
        ev.preventDefault();

        if (disabled) return;

        if (ev.dataTransfer.items) {
          const files = dataTransferItemsToFiles(ev.dataTransfer.items);

          handleFile(files);
        }
        setIsDragging(false);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDragEnter={() => {
        if (!disabled) {
          setIsDragging(true);
        }
      }}
      role='file-list-group'
      onDragOver={(ev) => ev.preventDefault()}
      className={cn(
        'hover:border-slate-700 hover:[&>*]:text-slate-700 relative transition-all rounded-sm border border-dashed border-stone-300 p-3',
        {
          'border-slate-700 [&>*]:text-slate-700': isDragging,
          'border-red-500': invalid,
          'border-primary border-solid': isFileLoaded,
        },
        className,
      )}
    >
      {!isDragging && (
        <input
          ref={inputRef}
          multiple={maxFiles > 1}
          onChange={(ev) => {
            if (ev.target.files?.length) {
              console.log('files to upload', ev.target.files);

              handleFile(Array.from(ev.target.files));
            }

            if (inputRef.current) {
              inputRef.current.value = '';
            }
          }}
          className={cn('opacity-0 absolute inset-0 w-full h-full', {
            'pointer-events-none touch-none': isDragging,
          })}
          disabled={disabled}
          type='file'
          id={`upload-file-${labelId}`}
        />
      )}

      <div
        role='file-list-group'
        className={cn('flex text-neutral-300 items-center flex-col space-y-3', {
          'pointer-events-none cursor-not-allowed': !disabled,
          'min-h-[10.75rem] justify-center': fromCreateClaim,
        })}
      >
        {isFileLoaded ? (
          preview ? (
            <img
              src={preview}
              alt='Preview'
              className='w-[5.563rem] h-[5.563rem] object-cover rounded-sm'
            />
          ) : (
            <NextEmptyStateIcon uploaded />
          )
        ) : fromCreateClaim ? (
          <NextEmptyStateIcon />
        ) : (
          <NextEmptyStateIcon />
        )}

        {isFileLoaded ? (
          children
        ) : (
          <>
            {label ? (
              <FileLabelBadge>{label}</FileLabelBadge>
            ) : (
              <p className='text-xs text-center text-foreground font-semibold'>
                {uploadMessage || ''}
              </p>
            )}

            <div className='text-center'>
              <p className='text-foreground font-medium text-2xs text-center'>
                Suelta aquí tu archivo o presiona para subirlo
              </p>

              <p className='text-stone-400 font-medium text-2xs mt-1'>
                {`Límite ${maxFiles} archivos - ${getHumanFileSize(
                  maxFileSize,
                )}`}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
